import type { FormEvent } from 'react';
import React, { useState } from 'react';
import { range } from 'lodash-es';
import styled from 'styled-components';

import {
  AdornedInput,
  Adornment,
  FormField,
  IconPlusOutline,
  Input,
  Label,
  Paragraph,
  Spacer,
  Text,
  TextButton,
} from '@zillow/constellation';
import { Center, Col, CurrencyInput, Row } from '@zillow/zrs-comet';

import { Field, FixturesAppliancesPersonalPropertySection } from '../../../models/index';
import { FIXTURES_APPLIANCES_PERSONAL_PROPERTY } from '../../../constants/index';
import { CenteredHR } from '../../Widgets/CenteredHR';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { stopPropagationIfInvalidNumber } from '../../../utils/validatorTypes/PositiveIntegerValidator';
import { isYesNoEnabledForNumberedFields } from '../../../constants/functionConstants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_ADDITIONAL_FIXTURES_APPLIANCES_PERSONAL_PROPERTY = 20;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  height: 1rem;
`;

type Props = {
  section: FixturesAppliancesPersonalPropertySection;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: FixturesAppliancesPersonalPropertySection) => void;
};

function FixturesAppliancesPersonalProperty({ section, setSectionValues, setSection }: Props): JSX.Element {
  const [hasClickedNo, setHasClickedNo] = useState(false);

  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: FixturesAppliancesPersonalPropertySection,
  });

  function onRemoveAll(): void {
    setHasClickedNo(true);
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function setName(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(FIXTURES_APPLIANCES_PERSONAL_PROPERTY.FIELD_NAME.NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setCost(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(FIXTURES_APPLIANCES_PERSONAL_PROPERTY.FIELD_NAME.COST, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderAdditionalFixturesAppliancesPersonalProperty(index: number): React.ReactNode {
    const nameField = section.getField(
      Field.getIndexedFieldName(FIXTURES_APPLIANCES_PERSONAL_PROPERTY.FIELD_NAME.NAME, index),
    );
    const costField = section.getField(
      Field.getIndexedFieldName(FIXTURES_APPLIANCES_PERSONAL_PROPERTY.FIELD_NAME.COST, index),
    );

    return (
      <Spacer marginBottom="sm">
        {index > 0 && (
          <Row marginY="xs">
            <Col xs={8}>
              <HeaderContainer>
                <Text fontType="bodyHeading">Included item #{index + 1}</Text>
              </HeaderContainer>
            </Col>
            <Col xs={4} align="flex-end">
              <HeaderContainer data-index={index} data-test="delete-fixture" onClick={onDeleteOne}>
                <TextButton>Delete</TextButton>
              </HeaderContainer>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12} sm={7} marginBottom="xs">
            <FormField marginBottom="xs">
              <Label id={`item-description-${index}`}>Item description</Label>
              <Input
                aria-labelledby={`item-description-${index}`}
                data-index={index}
                data-test="item-name"
                value={nameField.enteredValue}
                onChange={setName}
              />
            </FormField>
          </Col>
          <Col xs={12} sm={5}>
            <FormField>
              <Label id={`replacement-cost-${index}`}>Replacement cost</Label>
              <AdornedInput
                inputId={`Adorned-input-${costField}-${index}`}
                leftAdornment={<Adornment>$</Adornment>}
                input={
                  <CurrencyInput
                    aria-labelledby={`replacement-cost-${index}`}
                    data-index={index}
                    data-test="cost"
                    onKeyDown={stopPropagationIfInvalidNumber}
                    value={costField.enteredValue || ''}
                    onChange={setCost}
                    onBlur={setCost}
                  />
                }
              />
            </FormField>
          </Col>
        </Row>
      </Spacer>
    );
  }

  const totalFixturesAppliancesPersonalProperty = section.getTotalIndexes();
  const isEnabled = isYesNoEnabledForNumberedFields(
    section,
    'fixturesAppliancesPersonalProperty0_isEnabled',
    hasClickedNo,
  );

  if (totalFixturesAppliancesPersonalProperty == null) {
    return null;
  }

  return (
    <>
      <Text fontType="h6">Do you have any items on the property to itemize?</Text>
      <Paragraph marginBottom="sm">
        You must list all fixtures, appliances, personal property, and equipment, along with their replacement costs.
      </Paragraph>
      <YesAndNoExpansion yesHandler={onAddOne} noHandler={onRemoveAll} yesActive={isEnabled}>
        <Spacer marginTop="lg"></Spacer>
        {range(0, totalFixturesAppliancesPersonalProperty).map((index: number) => (
          <React.Fragment key={index}>
            {index > 0 && <CenteredHR />}
            {renderAdditionalFixturesAppliancesPersonalProperty(index)}
          </React.Fragment>
        ))}
        <Center>
          {totalFixturesAppliancesPersonalProperty < MAXIMUM_ADDITIONAL_FIXTURES_APPLIANCES_PERSONAL_PROPERTY ? (
            totalFixturesAppliancesPersonalProperty > 0 && (
              <TextButton data-test="add-another" icon={<IconPlusOutline />} onClick={onAddOne}>
                Add another item
              </TextButton>
            )
          ) : (
            <Text>
              <Center>
                <span data-test="maximum-reached">
                  You’ve added {MAXIMUM_ADDITIONAL_FIXTURES_APPLIANCES_PERSONAL_PROPERTY} items, which is the maximum
                  allowed.
                </span>
              </Center>
            </Text>
          )}
        </Center>
      </YesAndNoExpansion>
    </>
  );
}

export default FixturesAppliancesPersonalProperty;
